import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ADS_CONSENT_EVENT, ADS_PREFERENCES_EVENT, attributedLocation, getAdsConsent,
  isGoogleAdsConfigured, loadGoogleAdsTag, setAdsConsent, withoutGoogleClickIdentifiers,
} from '@/services/googleAdsService';

/** Basic consent mode: no Google script or measurement until the shopper accepts. */
export default function GoogleAdsConsent() {
  const [consent, setConsent] = useState(getAdsConsent);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const update = () => setConsent(getAdsConsent());
    const open = () => setPreferencesOpen(true);
    window.addEventListener(ADS_CONSENT_EVENT, update);
    window.addEventListener(ADS_PREFERENCES_EVENT, open);
    return () => {
      window.removeEventListener(ADS_CONSENT_EVENT, update);
      window.removeEventListener(ADS_PREFERENCES_EVENT, open);
    };
  }, []);

  useEffect(() => {
    if (!isGoogleAdsConfigured() || consent === 'unset') return;
    const href = consent === 'granted'
      ? attributedLocation(window.location.href)
      : withoutGoogleClickIdentifiers(window.location.href);
    if (href !== window.location.href) {
      const url = new URL(href);
      navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true, state: location.state });
      return;
    }
    if (consent === 'granted') void loadGoogleAdsTag();
  }, [consent, location.pathname, location.search, location.hash, location.state, navigate]);

  if (!isGoogleAdsConfigured() || (consent !== 'unset' && !preferencesOpen)) return null;

  const choose = (choice: 'granted' | 'denied') => {
    setAdsConsent(choice);
    setPreferencesOpen(false);
  };

  return (
    <section
      aria-label="Advertising measurement preferences"
      data-support-obstruction="consent"
      className="fixed bottom-20 left-3 right-3 z-[100] rounded-xl border bg-white p-4 shadow-lg sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-md"
    >
      <h2 className="font-semibold text-gray-900">Help us understand our ads</h2>
      <p className="mt-2 text-sm text-gray-600">
        Allow Google Ads measurement cookies to connect ad visits with purchases.
        We share the purchase amount, currency and a transaction reference, without your contact details.
        Personalized ads are off. Your choice won't affect checkout.
      </p>
      <p className="mt-2 text-sm text-gray-600">
        This controls Google Ads measurement. Read about our other tools in the{' '}
        <Link className="underline underline-offset-2" to="/privacy">Privacy Policy</Link>.
        You can change this choice there at any time.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" className="flex-1" onClick={() => choose('denied')}>Decline</Button>
        <Button className="flex-1 bg-jamaica-green" onClick={() => choose('granted')}>Allow measurement</Button>
      </div>
    </section>
  );
}
